import React, { ErrorInfo, ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import * as Sentry from "@sentry/react";
import { Button } from "@/shared/ui";
import {
  ExclamationTriangleIcon,
  ArrowPathIcon,
  HomeIcon,
  BugAntIcon,
} from "@heroicons/react/24/outline";
import { ROUTES } from "@/shared/core";
import { ReportErrorModal } from "./ReportErrorModal";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorFallbackPropsInternal {
  error: Error | null;
  errorInfo: ErrorInfo | null;
  onReset: () => void;
}

const ErrorFallback: React.FC<ErrorFallbackPropsInternal> = ({
  error,
  errorInfo,
  onReset,
}) => {
  const { t } = useTranslation();
  const isDev = import.meta.env.DEV;
  const [showReportModal, setShowReportModal] = useState(false);

  const handleGoHome = () => {
    window.location.href = ROUTES.HOME;
  };

  return (
    <div className="min-h-screen bg-romance-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-white rounded-lg shadow-lg p-8 border border-tiber-200">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
            <ExclamationTriangleIcon className="h-8 w-8 text-red-600" />
          </div>
          <h1 className="text-3xl font-bold text-tiber-900 mb-2">
            {t("errorBoundary.title")}
          </h1>
          <p className="text-tiber-600 mb-6">
            {t("errorBoundary.description")}
          </p>

          {isDev && error && (
            <div className="mt-6 p-4 bg-red-50 rounded-lg border border-red-200 text-left">
              <h3 className="font-semibold text-red-900 mb-2">
                {t("errorBoundary.errorDetails")}
              </h3>
              <p className="text-sm text-red-800 font-mono mb-2">
                {error.toString()}
              </p>
              {errorInfo && (
                <details className="mt-2">
                  <summary className="text-sm text-red-700 cursor-pointer">
                    {t("errorBoundary.stackTrace")}
                  </summary>
                  <pre className="mt-2 text-xs text-red-600 overflow-auto max-h-64">
                    {errorInfo.componentStack}
                  </pre>
                </details>
              )}
            </div>
          )}

          <div className="flex flex-wrap gap-3 justify-center mt-8">
            <Button
              variant="secondary"
              onClick={handleGoHome}
              leftIcon={<HomeIcon className="h-5 w-5" />}
            >
              {t("errorBoundary.goHome")}
            </Button>
            <Button
              variant="secondary"
              onClick={() => setShowReportModal(true)}
              leftIcon={<BugAntIcon className="h-5 w-5" />}
            >
              {t("errorBoundary.reportError")}
            </Button>
            <Button
              variant="primary"
              onClick={onReset}
              leftIcon={<ArrowPathIcon className="h-5 w-5" />}
            >
              {t("errorBoundary.reload")}
            </Button>
          </div>

          <div className="mt-6 text-sm text-tiber-500">
            <p>
              {t("errorBoundary.contactSupport")}
              <a
                href="mailto:nolimstudios@gmail.com"
                className="text-tiber-500 hover:underline"
              >
                {t("errorBoundary.contactLink")}
              </a>
            </p>
          </div>
        </div>
      </div>

      <ReportErrorModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        errorMessage={error?.message}
        errorStack={error?.stack}
      />
    </div>
  );
};

// Wrapper component to use hooks and Sentry ErrorBoundary
export const ErrorBoundary: React.FC<Props> = (props) => {
  // Use Sentry's ErrorBoundary for better integration
  // while keeping our custom fallback UI
  return (
    <Sentry.ErrorBoundary
      fallback={(errorData) => {
        if (props.fallback) {
          return <>{props.fallback}</>;
        }
        return (
          <ErrorFallback
            error={errorData.error instanceof Error ? errorData.error : null}
            errorInfo={
              errorData.componentStack
                ? ({ componentStack: errorData.componentStack } as ErrorInfo)
                : null
            }
            onReset={errorData.resetError}
          />
        );
      }}
      showDialog={false}
      beforeCapture={(scope, _error, errorInfo) => {
        scope.setContext("react", {
          componentStack:
            errorInfo &&
            typeof errorInfo === "object" &&
            "componentStack" in errorInfo
              ? (errorInfo as ErrorInfo).componentStack
              : undefined,
        });
      }}
    >
      {props.children}
    </Sentry.ErrorBoundary>
  );
};

// Extend Window interface for error tracker
declare global {
  interface Window {
    errorTracker?: {
      captureException: (
        error: Error,
        context?: Record<string, unknown>
      ) => void;
    };
  }
}
